import React from 'react';
import { Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { ChakraProvider } from '@chakra-ui/react';

import CareersLayout from './pages/Careers/Layout';
import CareersJobsList from './pages/Careers/JobsList';
import CareersJobDetail from './pages/Careers/JobDetail';
import CareersApply from './pages/Careers/Apply';
import CareersSignup from './pages/Careers/Signup';
import CareersLogin from './pages/Careers/Login';
import CareersVerifyEmail from './pages/Careers/VerifyEmail';
import CareersForgotPassword from './pages/Careers/ForgotPassword';
import CareersResetPassword from './pages/Careers/ResetPassword';
import CareersMyApplications from './pages/Careers/MyApplications';

/**
 * Keep links that were shared before this site had its own domain working.
 *
 * Job adverts get pasted into LinkedIn, emails and WhatsApp, and those URLs
 * carried the /careers prefix from when this was a section of the Hive portal.
 * They outlive the routing decision that produced them, so /careers/* is
 * preserved as a permanent redirect to the same page at the root. Query string
 * and hash are carried across — the login redirect parameter rides in the
 * query, and dropping it would land a returning applicant on the wrong page.
 */
function LegacyRedirect() {
  const { '*': rest } = useParams();
  const { search, hash } = useLocation();
  return <Navigate to={`/${rest || ''}${search}${hash}`} replace />;
}

/**
 * The public career site, served at the root of its own domain.
 *
 * Deliberately the whole application rather than a section of the Hive portal.
 * Serving the two together meant every visitor downloaded the entire internal
 * recruiting tool — requisitions, evaluations, the assessment editor — to read
 * a job advert: several megabytes of JavaScript nobody outside the company
 * should be sent, readable by anyone who opens devtools.
 *
 * There is no backend here. hive-back serves both, and these pages call the
 * public endpoints under /public/careers, which keep that prefix because they
 * are API routes rather than pages.
 */
function App() {
  return (
    <ChakraProvider>
      <Routes>
        <Route element={<CareersLayout />}>
          <Route path="/"                 element={<CareersJobsList />} />
          <Route path="/jobs/:req_no"     element={<CareersJobDetail />} />
          <Route path="/apply/:req_no"    element={<CareersApply />} />
          <Route path="/signup"           element={<CareersSignup />} />
          <Route path="/login"            element={<CareersLogin />} />
          <Route path="/verify-email"     element={<CareersVerifyEmail />} />
          <Route path="/forgot-password"  element={<CareersForgotPassword />} />
          <Route path="/reset-password"   element={<CareersResetPassword />} />
          <Route path="/my-applications"  element={<CareersMyApplications />} />
        </Route>

        {/* Links shared before the domain split. */}
        <Route path="/careers/*" element={<LegacyRedirect />} />
        <Route path="/careers"   element={<Navigate to="/" replace />} />

        {/* Anything else is a mistyped or dead job link; the job list is the
            most useful place to land rather than a bare 404. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ChakraProvider>
  );
}

export default App;
